import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { extractBeijingTime } from '../utils/timeUtils';
import {
  Code2,
  CheckCircle,
  XCircle,
  Award,
  FileCode,
  Target,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Terminal,
  Cpu,
  MemoryStick,
  Hammer
} from 'lucide-react';
import { supabase } from '../supabase/client';
import QuestionHtmlWithZoom from './QuestionHtmlWithZoom';
import CppCodeEditor from './CppCodeEditor';
import KnowledgeTagPills from './KnowledgeTagPills';
import type { TagDef } from '../utils/questionTag';

// Question 类型定义（与 ExamReviewPage 兼容）
interface Question {
  id: string;
  type: string;
  title: string;
  content?: string;
  score: number;
  userAnswer?: string;
  reference_answer?: string;
}

interface CppCodingResultDetailProps {
  question: Question;
  paperId: string;
  answer: string; // submissionId
  teacherFeedback?: { score?: number; feedback?: string }; // 教师评分数据
  selectedKnowledgeTags?: TagDef[]; // 本题考查的知识点标签（复盘只读展示）
}

interface SubmissionDetail {
  id: string;
  code: string;
  status: 'pending' | 'grading' | 'graded' | 'error';
  score?: number;
  maxScore?: number;
  executionTimeMs?: number;
  memoryUsedMb?: number;
  compileOutput?: string;
  executionOutput?: string;
  testCaseResults?: TestCaseResult[];
  submittedAt: string;
  feedback?: string;
  gradedBy?: 'ai' | 'teacher';
}

interface TestCaseResult {
  testCaseId: string;
  passed: boolean;
  input?: string;
  expectedOutput?: string;
  actualOutput?: string;
  errorMessage?: string;
  executionTimeMs?: number;
}

interface QuestionDetail {
  id: string;
  content: string;
  description?: string;
  sampleInput?: string;
  sampleOutput?: string;
  referenceSolution?: string;
  timeLimitMs: number;
  memoryLimitMb: number;
  score: number;
  difficulty?: string;
}

interface TestCase {
  id: string;
  input: string;
  expectedOutput: string;
  isHidden: boolean;
  scoreWeight: number;
}

// 空数据提示组件
function EmptyHint({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div className="text-center py-8 text-gray-400">
      <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
      <p className="text-gray-500">{text}</p>
    </div>
  );
}

export default function CppCodingResultDetail({
  question,
  paperId,
  answer,
  teacherFeedback,
  selectedKnowledgeTags,
}: CppCodingResultDetailProps) {
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(null);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    answer: true,
    grading: true,
    question: true,
    reference: true,
    rules: true,
  });

  useEffect(() => {
    loadData();
  }, [answer, question.id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadSubmission(), loadQuestionDetail(), loadTestCases()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmission = async () => {
    // 如果没有答案，清空提交状态并直接返回
    if (!answer || answer.trim() === '') {
      setSubmission(null);
      return;
    }

    // 通过 submissionId 查询
    const { data, error } = await supabase
      .from('programming_submissions')
      .select('*')
      .eq('id', answer)
      .maybeSingle();

    if (error) throw new Error(`加载提交信息失败: ${error.message}`);

    if (data) {
      // 从 auto_grade_result 中提取执行详情
      const autoResult = data.auto_grade_result as Record<string, any> | null;

      let finalScore = data.score ?? undefined;
      let finalFeedback = data.feedback || undefined;
      let gradedBy: 'teacher' | undefined = undefined;

      // 判断评分来源：教师评分
      if (data.status === 'graded' && data.score != null) {
        gradedBy = 'teacher';
      }

      // 使用通过 props 传递的教师评分数据
      if (teacherFeedback) {
        if (teacherFeedback.score !== undefined) {
          finalScore = teacherFeedback.score;
          gradedBy = 'teacher';
        }
        if (teacherFeedback.feedback) {
          finalFeedback = teacherFeedback.feedback;
        }
      }

      setSubmission({
        id: data.id,
        code: data.code_content || '',
        status: (data.status as 'pending' | 'grading' | 'graded' | 'error') || 'pending',
        score: finalScore,
        maxScore: question.score,
        executionTimeMs: autoResult?.execution_time_ms ?? undefined,
        memoryUsedMb: autoResult?.memory_used_mb ?? undefined,
        compileOutput: autoResult?.compile_output || undefined,
        executionOutput: autoResult?.execution_output || undefined,
        testCaseResults: autoResult?.test_case_results ? (autoResult.test_case_results as unknown as TestCaseResult[]) : undefined,
        submittedAt: data.submitted_at || data.created_at || '',
        feedback: finalFeedback,
        gradedBy: gradedBy,
      });
    }
  };

  const loadQuestionDetail = async () => {
    // 优先从 questions 表获取 C++ 编程题数据（标准存储位置）
    const { data: questionsData, error: questionsError } = await supabase
      .from('questions')
      .select('content, reference_answer, explanation, scoring_points')
      .eq('id', question.id)
      .single();

    if (questionsError) {
      console.warn('从 questions 表加载 C++ 题目详情失败:', questionsError.message);
      // 如果 questions 表也查不到，尝试从 cpp_questions 表获取（兼容旧数据）
      const { data: cppData, error: cppError } = await supabase
        .from('cpp_questions')
        .select('title, description, sample_input, sample_output, time_limit_ms, memory_limit_mb, difficulty')
        .eq('id', question.id)
        .single();

      if (cppError) {
        console.warn('从 cpp_questions 表加载也失败:', cppError.message);
        // 使用 question prop 中的基础信息作为兜底
        setQuestionDetail({
          id: question.id,
          content: question.content || '',
          referenceSolution: question.reference_answer || undefined,
          timeLimitMs: 1000,
          memoryLimitMb: 64,
          score: question.score,
        });
        return;
      }

      if (cppData) {
        setQuestionDetail({
          id: question.id,
          content: cppData.title || '',
          description: cppData.description || undefined,
          sampleInput: cppData.sample_input || undefined,
          sampleOutput: cppData.sample_output || undefined,
          timeLimitMs: cppData.time_limit_ms || 1000,
          memoryLimitMb: cppData.memory_limit_mb || 64,
          score: question.score,
          difficulty: cppData.difficulty || undefined,
        });
      }
      return;
    }

    if (questionsData) {
      setQuestionDetail({
        id: question.id,
        content: questionsData.content || '',
        referenceSolution: questionsData.reference_answer || questionsData.explanation || undefined,
        timeLimitMs: 1000,
        memoryLimitMb: 64,
        score: question.score,
      });
    }
  };

  const loadTestCases = async () => {
    const { data, error } = await supabase
      .from('cpp_test_cases')
      .select('id, input, expected_output, is_hidden, score')
      .eq('question_id', question.id);

    if (error) {
      // 测试用例可能不存在，不抛出错误
      return;
    }

    if (data) {
      setTestCases(data.map(tc => ({
        id: tc.id,
        input: tc.input || '',
        expectedOutput: tc.expected_output || '',
        isHidden: tc.is_hidden || false,
        scoreWeight: tc.score || 1,
      })));
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  };

  // 统一评分判断逻辑
  const isGraded = teacherFeedback?.score != null || (submission?.status === 'graded' && submission?.score != null);
  const gradedBy = teacherFeedback?.score != null ? 'teacher' : submission?.gradedBy;
  const displayScore = teacherFeedback?.score ?? submission?.score ?? 0;
  const maxScore = submission?.maxScore ?? question.score;
  const displayFeedback = teacherFeedback?.feedback ?? submission?.feedback ?? '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">加载中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg p-4 border border-red-200 flex items-center gap-2 text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  // 判断各区域是否有数据
  const hasAnswerData = !!submission?.code;
  const hasReferenceData = !!questionDetail?.referenceSolution || !!questionDetail?.sampleOutput;
  const hasRulesData = testCases.length > 0 || !!questionDetail?.timeLimitMs;

  return (
    <div className="space-y-4">
      {/* 你的答案区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('answer')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileCode className="w-5 h-5 text-green-600" />
            <span className="font-medium text-gray-900">你的答案</span>
            {submission && (
              <span className="text-sm text-gray-500">
                提交时间: {formatDateTime(submission.submittedAt)}
              </span>
            )}
          </div>
          {expandedSections.answer ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.answer && (
          <div className="p-4">
            {hasAnswerData ? (
              <div className="space-y-4">
                <CppCodeEditor
                  code={submission.code}
                  readOnly={true}
                  height="300px"
                />
              </div>
            ) : (
              <EmptyHint icon={Code2} text="未提交答案" />
            )}
          </div>
        )}
      </motion.div>

      {/* 评分结果区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('grading')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-gray-900">评分结果</span>
            {isGraded && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                gradedBy === 'teacher'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {gradedBy === 'teacher' ? '教师评分' : '已评分'}
              </span>
            )}
          </div>
          {expandedSections.grading ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.grading && (
          <div className="p-4">
            {submission ? (
              <div className="space-y-3">
                {isGraded ? (
                  <>
                    {/* 分数展示区 */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                        <div className="text-xs text-blue-600 mb-1">本题分值</div>
                        <div className="text-2xl font-bold text-blue-700">{maxScore}<span className="text-sm font-normal ml-1">分</span></div>
                      </div>
                      <div className={`bg-gradient-to-br rounded-lg p-4 border ${
                        displayScore > 0
                          ? 'from-green-50 to-green-100 border-green-200'
                          : 'from-red-50 to-red-100 border-red-200'
                      }`}>
                        <div className={`text-xs mb-1 ${displayScore > 0 ? 'text-green-600' : 'text-red-600'}`}>你的得分</div>
                        <div className={`text-2xl font-bold ${displayScore > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {displayScore}<span className="text-sm font-normal ml-1">分</span>
                        </div>
                      </div>
                    </div>
                    {/* 评语 */}
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-gray-600 w-16 flex-shrink-0">评语：</span>
                      <span className="text-sm text-gray-800 whitespace-pre-wrap">
                        {displayFeedback || '暂无评语'}
                      </span>
                    </div>
                    {/* 执行信息 */}
                    {(submission.executionTimeMs || submission.memoryUsedMb) && (
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {submission.executionTimeMs && (
                          <span className="flex items-center gap-1">
                            <Cpu className="w-4 h-4" />
                            执行时间: {submission.executionTimeMs}ms
                          </span>
                        )}
                        {submission.memoryUsedMb && (
                          <span className="flex items-center gap-1">
                            <MemoryStick className="w-4 h-4" />
                            内存使用: {submission.memoryUsedMb}MB
                          </span>
                        )}
                      </div>
                    )}
                    {/* 编译输出 */}
                    {submission.compileOutput && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                          <Hammer className="w-4 h-4" />
                          编译输出
                        </h4>
                        <pre className="text-sm text-gray-700 bg-gray-100 p-3 rounded overflow-x-auto">
                          {submission.compileOutput}
                        </pre>
                      </div>
                    )}
                    {/* 执行输出 */}
                    {submission.executionOutput && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                          <Terminal className="w-4 h-4" />
                          执行输出
                        </h4>
                        <pre className="text-sm text-gray-700 bg-gray-100 p-3 rounded overflow-x-auto">
                          {submission.executionOutput}
                        </pre>
                      </div>
                    )}
                    {/* 测试用例结果 */}
                    {submission.testCaseResults && submission.testCaseResults.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-900">测试用例结果</h4>
                        <div className="space-y-2">
                          {submission.testCaseResults.map((result, idx) => (
                            <div
                              key={idx}
                              className={`flex items-start gap-3 p-3 rounded-lg ${
                                result.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                              }`}
                            >
                              {result.passed ? (
                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                              )}
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-900">测试用例 {idx + 1}</span>
                                  <span className={`text-sm font-medium ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                                    {result.passed ? '通过' : '失败'}
                                  </span>
                                </div>
                                {!result.passed && result.errorMessage && (
                                  <p className="text-sm text-red-600 mt-1">{result.errorMessage}</p>
                                )}
                                {result.executionTimeMs && (
                                  <p className="text-sm text-gray-500 mt-1">
                                    执行时间: {result.executionTimeMs}ms
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* 分数展示区 - 待批阅 */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                        <div className="text-xs text-blue-600 mb-1">本题分值</div>
                        <div className="text-2xl font-bold text-blue-700">{maxScore}<span className="text-sm font-normal ml-1">分</span></div>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                        <div className="text-xs text-amber-600 mb-1">你的得分</div>
                        <div className="text-2xl font-bold text-amber-700">待批阅</div>
                      </div>
                    </div>
                    {/* 评语 */}
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-gray-600 w-16 flex-shrink-0">评语：</span>
                      <span className="text-sm text-gray-500">暂无评语</span>
                    </div>
                  </>
                )}
              </div>
            ) : teacherFeedback ? (
              <>
                {/* 有教师评分但无提交记录 */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="text-xs text-blue-600 mb-1">本题分值</div>
                    <div className="text-2xl font-bold text-blue-700">{maxScore}<span className="text-sm font-normal ml-1">分</span></div>
                  </div>
                  <div className={`bg-gradient-to-br rounded-lg p-4 border ${
                    teacherFeedback.score > 0
                      ? 'from-green-50 to-green-100 border-green-200'
                      : 'from-red-50 to-red-100 border-red-200'
                  }`}>
                    <div className={`text-xs mb-1 ${teacherFeedback.score > 0 ? 'text-green-600' : 'text-red-600'}`}>你的得分</div>
                    <div className={`text-2xl font-bold ${teacherFeedback.score > 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {teacherFeedback.score ?? 0}<span className="text-sm font-normal ml-1">分</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-gray-600 w-16 flex-shrink-0">评语：</span>
                  <span className="text-sm text-gray-800 whitespace-pre-wrap">{teacherFeedback.feedback || '暂无评语'}</span>
                </div>
              </>
            ) : (
              <>
                {/* 未提交 */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-16">评分：</span>
                  <span className="font-bold text-red-600">0/{maxScore}分</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-gray-600 w-16">评语：</span>
                  <span className="text-sm text-gray-600">此题未作答</span>
                </div>
              </>
            )}
          </div>
        )}
      </motion.div>

      {/* 参考答案区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('reference')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-gray-900">参考答案</span>
          </div>
          {expandedSections.reference ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.reference && (
          <div className="p-4">
            <KnowledgeTagPills selected={selectedKnowledgeTags} />
            {hasReferenceData ? (
              <div className="space-y-4">
                {questionDetail?.referenceSolution && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">参考实现说明</h4>
                    <QuestionHtmlWithZoom
                      html={questionDetail.referenceSolution || '<p>暂无参考答案说明</p>'}
                    />
                  </div>
                )}
                {(questionDetail?.sampleInput || questionDetail?.sampleOutput) && (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-2">示例</h4>
                    {questionDetail.sampleInput && (
                      <div className="mb-2">
                        <span className="text-sm text-gray-500">输入:</span>
                        <pre className="mt-1 text-sm bg-white p-2 rounded border border-gray-200">{questionDetail.sampleInput}</pre>
                      </div>
                    )}
                    {questionDetail.sampleOutput && (
                      <div>
                        <span className="text-sm text-gray-500">输出:</span>
                        <pre className="mt-1 text-sm bg-white p-2 rounded border border-gray-200">{questionDetail.sampleOutput}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <EmptyHint icon={CheckCircle} text="暂无参考答案" />
            )}
          </div>
        )}
      </motion.div>

      {/* 评分规则区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('rules')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-indigo-600" />
            <span className="font-medium text-gray-900">评分规则</span>
          </div>
          {expandedSections.rules ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.rules && (
          <div className="p-4">
            {hasRulesData ? (
              <div className="space-y-4">
                {/* 资源限制 */}
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <h4 className="font-medium text-indigo-900 mb-2">资源限制</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white rounded p-3 border border-gray-200">
                      <span className="text-gray-500">时间限制:</span>
                      <span className="ml-2 text-gray-900">{questionDetail?.timeLimitMs || 1000}ms</span>
                    </div>
                    <div className="bg-white rounded p-3 border border-gray-200">
                      <span className="text-gray-500">内存限制:</span>
                      <span className="ml-2 text-gray-900">{questionDetail?.memoryLimitMb || 64}MB</span>
                    </div>
                  </div>
                </div>

                {/* 测试用例 */}
                {testCases.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">测试用例</h4>
                    <div className="space-y-2">
                      {testCases.map((tc, idx) => (
                        <div key={tc.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-700">用例 {idx + 1}</span>
                            <span className="text-xs text-gray-500">
                              权重: {tc.scoreWeight}
                            </span>
                          </div>
                          {!tc.isHidden ? (
                            <>
                              <div className="text-sm mb-1">
                                <span className="text-gray-500">输入:</span>
                                <code className="ml-2 bg-gray-100 px-2 py-0.5 rounded">{tc.input}</code>
                              </div>
                              <div className="text-sm">
                                <span className="text-gray-500">期望输出:</span>
                                <code className="ml-2 bg-gray-100 px-2 py-0.5 rounded">{tc.expectedOutput}</code>
                              </div>
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">隐藏用例</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">暂无测试用例</div>
                )}
              </div>
            ) : (
              <EmptyHint icon={Target} text="暂无评分规则" />
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
